import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getIndustries, getCategories, getDocuments, deleteDocument } from '../../services/industry-insights';
import AdminRoute from '../../components/AdminRoute';

// Define the note type that includes all metadata
interface Note {
  filename: string;
  original_filename: string;
  title: string;
  description?: string;
  tags?: string[];
  upload_date?: string;
  last_modified: string;
  url: string;
}

const IndustryInsightsPage: React.FC = () => {
  const [industries, setIndustries] = useState<{ name: string; display_name: string }[]>([]);
  const [selectedIndustry, setSelectedIndustry] = useState<string>('');
  const [categories, setCategories] = useState<{ name: string; display_name: string }[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [notes, setNotes] = useState<Note[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [noteToDelete, setNoteToDelete] = useState<Note | null>(null);

  // Load industries on component mount
  useEffect(() => {
    const loadIndustries = async () => {
      try {
        setLoading(true);
        const response = await getIndustries();
        setIndustries(response.industries);
        setLoading(false);
      } catch (err) {
        setError('Failed to load industries');
        setLoading(false);
        console.error(err);
      }
    };

    loadIndustries();
  }, []);

  // Load categories when an industry is selected
  useEffect(() => {
    if (!selectedIndustry) {
      setCategories([]);
      return;
    }

    const loadCategories = async () => {
      try {
        setLoading(true);
        const response = await getCategories(selectedIndustry);
        setCategories(response.categories);
        setLoading(false);
      } catch (err) {
        setError('Failed to load categories');
        setLoading(false);
        console.error(err);
      }
    };

    loadCategories();
  }, [selectedIndustry]);

  // Load notes when both industry and category are selected
  useEffect(() => {
    if (!selectedIndustry || !selectedCategory) {
      setNotes([]);
      return;
    }

    const loadNotes = async () => {
      try {
        setLoading(true);
        const response = await getDocuments(selectedIndustry, selectedCategory);
        
        // Transform documents to notes with more friendly structure
        const notesList = response.documents.map(doc => ({
          filename: doc.filename,
          original_filename: doc.original_filename,
          title: doc.metadata?.title || doc.original_filename,
          description: doc.metadata?.description,
          tags: doc.metadata?.tags,
          upload_date: doc.upload_date,
          last_modified: doc.last_modified,
          url: doc.url
        }));
        
        setNotes(notesList);
        setLoading(false);
      } catch (err) {
        setError('Failed to load notes');
        setLoading(false);
        console.error(err);
      }
    };

    loadNotes();
  }, [selectedIndustry, selectedCategory]);

  const handleIndustryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedIndustry(e.target.value);
    setSelectedCategory('');
  };

  const handleCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedCategory(e.target.value);
  };

  const handleDeleteClick = (note: Note) => {
    setNoteToDelete(note);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!noteToDelete || !selectedIndustry || !selectedCategory) return;
    
    try {
      setLoading(true);
      await deleteDocument(selectedIndustry, selectedCategory, noteToDelete.filename);
      
      // Remove the deleted note from the list
      setNotes(notes.filter(note => note.filename !== noteToDelete.filename));
      
      setShowDeleteModal(false);
      setNoteToDelete(null);
      setLoading(false);
    } catch (err) {
      setError('Failed to delete note');
      setLoading(false);
      console.error(err);
    }
  };

  return (
    <AdminRoute>
      <div className="container mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-800">Industry Insights Management</h1>
          <Link 
            to="/admin/industry-insights/new" 
            className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
          >
            Add New Note
          </Link>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div>
              <label htmlFor="industry" className="block text-sm font-medium text-gray-700 mb-1">
                Select Industry
              </label>
              <select
                id="industry"
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={selectedIndustry}
                onChange={handleIndustryChange}
              >
                <option value="">Select an industry</option>
                {industries.map((industry) => (
                  <option key={industry.name} value={industry.name}>
                    {industry.display_name}
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1">
                Select Category
              </label>
              <select
                id="category"
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={selectedCategory}
                onChange={handleCategoryChange}
                disabled={!selectedIndustry}
              >
                <option value="">Select a category</option>
                {categories.map((category) => (
                  <option key={category.name} value={category.name}>
                    {category.display_name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-blue-600 border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]" role="status">
              <span className="!absolute !-m-px !h-px !w-px !overflow-hidden !whitespace-nowrap !border-0 !p-0 ![clip:rect(0,0,0,0)]">Loading...</span>
            </div>
            <p className="mt-2 text-gray-600">Loading...</p>
          </div>
        ) : error ? (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-6" role="alert">
            <strong className="font-bold">Error:</strong>
            <span className="block sm:inline"> {error}</span>
          </div>
        ) : notes.length > 0 ? (
          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Title
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Last Modified
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tags
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {notes.map((note) => (
                  <tr key={note.filename}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{note.title}</div>
                      {note.description && (
                        <div className="text-sm text-gray-500 truncate max-w-md">{note.description}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">
                        {new Date(note.last_modified).toLocaleDateString()} {new Date(note.last_modified).toLocaleTimeString()}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-wrap gap-1">
                        {note.tags?.map((tag, index) => (
                          <span key={index} className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800">
                            {tag}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <Link
                        to={`/admin/industry-insights/edit/${selectedIndustry}/${selectedCategory}/${note.filename}`}
                        className="text-blue-600 hover:text-blue-900 mr-4"
                      >
                        Edit
                      </Link>
                      <button
                        onClick={() => handleDeleteClick(note)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : selectedIndustry && selectedCategory ? (
          <div className="text-center py-12 bg-white rounded-lg shadow-lg">
            <p className="text-gray-500">No notes found for this industry and category.</p>
            <Link
              to="/admin/industry-insights/new"
              className="mt-4 inline-block bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
              state={{ industry: selectedIndustry, category: selectedCategory }}
            >
              Add your first note
            </Link>
          </div>
        ) : (
          <div className="text-center py-12 bg-white rounded-lg shadow-lg">
            <p className="text-gray-500">Select an industry and category to view notes.</p>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full">
              <h3 className="text-xl font-bold mb-4">Confirm Deletion</h3>
              <p className="mb-6">
                Are you sure you want to delete "{noteToDelete?.title}"? This action cannot be undone.
              </p>
              <div className="flex justify-end gap-4">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDelete}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminRoute>
  );
};

export default IndustryInsightsPage;
